import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const feeVariantId = '47638346661972';
const eligibleProductIds = new Set(["10009493864532","10273932738644","7845485903956","7841658077268","7836483878996","10274175287380","9352476590164","7862467133524","10179911385172","7893386002516","7909808242772","7904060112980","7903021563988","7902271242324","10280050098260","7862505341012","9361628954708","7840394084436","7890480267348","7849575514196","10233151782996","7840347390036","10236605104212","10201013321812"]);
const context = 'cwt-small-order-fee';
let syncTimer;
let syncing = false;
let rerun = false;

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

function eligibleQuantity(cart) {
  return cart.items.reduce((total, item) => {
    if (String(item.variant_id) === feeVariantId) return total;
    return eligibleProductIds.has(String(item.product_id)) ? total + item.quantity : total;
  }, 0);
}

async function changeFee(cart, desiredQuantity) {
  const feeItem = cart.items.find((item) => String(item.variant_id) === feeVariantId);
  const sections = sectionIds();
  const sectionsValue = sections.join(',');
  let response;
  let action;

  if (!feeItem && desiredQuantity === 1) {
    const body = new FormData();
    body.set('id', feeVariantId);
    body.set('quantity', '1');
    if (sectionsValue) body.set('sections', sectionsValue);
    body.set('sections_url', window.location.pathname);
    const config = fetchConfig('javascript', { body });
    response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    });
    action = 'add';
  } else if (feeItem && feeItem.quantity !== desiredQuantity) {
    const body = JSON.stringify({
      id: feeItem.key,
      quantity: desiredQuantity,
      sections: sectionsValue,
      sections_url: window.location.pathname,
    });
    response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    action = desiredQuantity === 0 ? 'remove' : 'update';
  } else {
    return;
  }

  const result = await response.json();
  if (!response.ok || result.status || result.errors) {
    throw new Error(result.description || result.message || result.errors || 'Small-order fee update failed');
  }

  const updatedCart = await fetchCart();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context,
    lines: [{ id: feeItem?.key || feeVariantId, quantity: desiredQuantity }],
    promise: deferred.promise,
  }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
    detail: {
      sections: result.sections,
      items: updatedCart.items,
      itemCount: updatedCart.item_count,
      source: context,
      didError: false,
    },
  });
}

async function syncFee() {
  if (syncing) {
    rerun = true;
    return;
  }

  syncing = true;
  try {
    const cart = await fetchCart();
    const desiredQuantity = eligibleQuantity(cart) === 1 ? 1 : 0;
    await changeFee(cart, desiredQuantity);
  } catch (error) {
    console.error('[CWT] Small-order fee synchronization failed', error);
  } finally {
    syncing = false;
    if (rerun) {
      rerun = false;
      scheduleSync();
    }
  }
}

function scheduleSync() {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(syncFee, 100);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.detail?.context === context) return;
  if (event.promise) {
    event.promise.then(scheduleSync).catch(() => {});
  } else {
    scheduleSync();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
} else {
  scheduleSync();
}
